import { ICON } from '@/constants/icons';
import { INTRUSION_PARTITION_STATUSES, INTRUSION_ZONE_STATUSES } from '@/views/intrusion/constants';
import type {
  IntrusionPartitionStatusFlag,
  IntrusionZoneStatusFlag,
  ZonePartitionArmState
} from '@/views/intrusion/types';

import { PARTITION_STATUS_RULES, ZONE_STATUS_RULES } from './constants';
import type { StatusCellVariant } from './status-variant';
import type {
  IntrusionPartitionSmartIconSummary,
  IntrusionSmartIconStatus,
  IntrusionZoneSmartIconSummary,
  SmartIconStatusRule
} from './types';

export type { IntrusionPartitionSmartIconSummary, IntrusionZoneSmartIconSummary };

/** A zone row as the aggregate builder needs it: its own flags plus its partitions' arm state. */
type ZoneSummarySource = {
  statusFlags: readonly IntrusionZoneStatusFlag[];
  partitionArmState?: ZonePartitionArmState;
};

/**
 * Aggregate a list of partitions into the count summary the resolver/aggregate-descriptor consume.
 * `count` defaults to the list length but callers with an authoritative total (e.g. the panel's
 * partition count) can pass it explicitly while coloring is still derived from the rows present.
 */
export const buildPartitionSmartIconSummary = (
  partitions: readonly { statusFlags?: readonly IntrusionPartitionStatusFlag[] }[],
  count: number = partitions.length
): IntrusionPartitionSmartIconSummary => {
  const countWith = (flag: IntrusionPartitionStatusFlag): number =>
    partitions.filter((partition) => partition.statusFlags?.includes(flag)).length;
  const delayCount =
    countWith(INTRUSION_PARTITION_STATUSES.EXIT_DELAY) +
    countWith(INTRUSION_PARTITION_STATUSES.ENTRY_DELAY);

  return {
    count,
    armedCount:
      countWith(INTRUSION_PARTITION_STATUSES.ARMED_AWAY) +
      countWith(INTRUSION_PARTITION_STATUSES.ARMED_STAY) +
      countWith(INTRUSION_PARTITION_STATUSES.ARMED_NIGHT),
    notReadyCount: countWith(INTRUSION_PARTITION_STATUSES.NOT_READY),
    alarmCount: countWith(INTRUSION_PARTITION_STATUSES.ALARM),
    delayCount,
    hasStatusData: partitions.length > 0
  };
};

/**
 * Aggregate a list of zones into the count summary. `count` defaults to the list length; callers with
 * an authoritative total (the panel's zone count, a partition's child count) can pass it explicitly
 * while coloring is derived from the rows present.
 */
export const buildZoneSmartIconSummary = (
  zones: readonly ZoneSummarySource[],
  count: number = zones.length
): IntrusionZoneSmartIconSummary => {
  const countWith = (flag: IntrusionZoneStatusFlag): number =>
    zones.filter((zone) => zone.statusFlags.includes(flag)).length;
  const violatedCount = zones.filter(
    (zone) =>
      zone.partitionArmState === 'armed' &&
      zone.statusFlags.includes(INTRUSION_ZONE_STATUSES.OPEN) &&
      !zone.statusFlags.includes(INTRUSION_ZONE_STATUSES.BYPASSED)
  ).length;

  return {
    count,
    alarmCount: countWith(INTRUSION_ZONE_STATUSES.ALARM),
    troubleCount: countWith(INTRUSION_ZONE_STATUSES.TROUBLE),
    openCount: countWith(INTRUSION_ZONE_STATUSES.OPEN),
    bypassedCount: countWith(INTRUSION_ZONE_STATUSES.BYPASSED),
    violatedCount,
    hasStatusData: zones.length > 0
  };
};

const toCount = (value: number | string | undefined): number => {
  const count = Number(value ?? 0);

  return Number.isFinite(count) && count > 0 ? count : 0;
};

const resolveSmartIconStatus = <Context>(
  rules: ReadonlyArray<SmartIconStatusRule<Context>>,
  context: Context
): Omit<IntrusionSmartIconStatus, 'icon'> => {
  const rule = rules.find(({ test }) => test(context));

  return { variant: rule!.variant, label: rule!.label(context) };
};

const getIntrusionPartitionSmartIconStatus = ({
  count,
  armedCount,
  notReadyCount,
  alarmCount,
  delayCount,
  hasStatusData
}: IntrusionPartitionSmartIconSummary): IntrusionSmartIconStatus => ({
  icon: ['fas', ICON.LAYER_GROUP],
  ...resolveSmartIconStatus(PARTITION_STATUS_RULES, {
    partitionCount: toCount(count),
    armedPartitions: toCount(armedCount),
    notReadyPartitions: toCount(notReadyCount),
    alarmPartitions: toCount(alarmCount),
    delayPartitions: toCount(delayCount),
    hasStatusData
  })
});

const getIntrusionZoneSmartIconStatus = ({
  count,
  alarmCount,
  troubleCount,
  openCount,
  bypassedCount,
  violatedCount,
  hasStatusData
}: IntrusionZoneSmartIconSummary): IntrusionSmartIconStatus => ({
  icon: ['fas', ICON.SENSOR],
  ...resolveSmartIconStatus(ZONE_STATUS_RULES, {
    zoneCount: toCount(count),
    alarmZones: toCount(alarmCount),
    troubleZones: toCount(troubleCount),
    openZones: toCount(openCount),
    bypassedZones: toCount(bypassedCount),
    violatedZones: toCount(violatedCount),
    hasStatusData
  })
});

export interface AggregateStatusDescriptor {
  variant: StatusCellVariant;
  label: string;
  icon: ['fas', string];
  count: number;
}

export const getPartitionAggregateDescriptor = (
  summary: IntrusionPartitionSmartIconSummary
): AggregateStatusDescriptor => {
  const status = getIntrusionPartitionSmartIconStatus(summary);

  return { variant: status.variant, label: status.label, icon: status.icon, count: toCount(summary.count) };
};

export const getZoneAggregateDescriptor = (summary: IntrusionZoneSmartIconSummary): AggregateStatusDescriptor => {
  const status = getIntrusionZoneSmartIconStatus(summary);

  return { variant: status.variant, label: status.label, icon: status.icon, count: toCount(summary.count) };
};
